import {groupBy, groupByUniqueKey, isUuid, mapObject, objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcedureEdgeType, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {parseAndValidateAnalysisTemplateConfigData} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigTypes';
import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {
  createEdgeId,
  createHandleIdForMaterial,
  createHandleIdForMethod,
} from 'procedures/design/handleIdentifierUtils';
import {getMethodNamePlaceholder} from 'procedures/design/procedureMethodNodeCommonConsts';
import {ParameterSettings} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {validateMethodName} from 'procedures/design/validation/methodValidationUtils';
import {
  ProcedurePageFlowchartVersionQuery_procedureFlowchartVersion_ProcedureFlowchartVersionNode as ProcedureFlowchartVersionNode,
  ProcedurePageFlowchartVersionQuery,
} from 'procedures/graphql/__generated/ProcedurePageFlowchartVersionQuery';
import {
  EquipmentTypedModel,
  getMethodComponentCid,
  InaccessibleProcedureAnalysisTemplateConfiguration,
  InaccessibleProcedureTestDefinition,
  MaterialEdge,
  Method,
  MethodEdge,
  MethodNode,
  ParameterUsages,
  ProcedureAnalysisTemplateConfiguration,
  ProcedureDataParameterPlannedValue,
  ProcedureDataParameterSetting,
  ProcedureEdge,
  ProcedureEquipmentPlannedValue,
  ProcedureEquipmentSetting,
  ProcedureMaterialInputPlannedValue,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
  ProcedureNodeType,
  ProcedureStepConfig,
  ProcedureStepGroup,
  ProcedureTestDefinition,
  ProcedureWithMetadata,
} from 'procedures/ProcedureDomainTypes';
import {NetworkProcedure} from 'procedures/ProcedurePageComponents';
import {
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
  isDataParameterFieldType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import * as logging from 'util/Logging';
import {omit} from 'util/omitKey';
import pick from 'util/pick';

import {FullProcedureMethodDefinitionVersionGraphQL} from '../../../graphql-fragments/procedures/__generated/FullProcedureMethodDefinitionVersionGraphQL';

type DataParameterSettingGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['dataParameterSettings'][number];

/** Single data-parameter setting from network; version.libraryParameterDefinition may be omitted (e.g. from parameter-versions fragment). */
interface NetworkDataParameterSetting {
  version: Omit<DataParameterSettingGraphQL['version'], 'libraryParameterDefinition'> & {
    libraryParameterDefinition?: DataParameterSettingGraphQL['version']['libraryParameterDefinition'] | null;
  };
  values: DataParameterSettingGraphQL['values'];
}

type DataParameterPlannedValueGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['dataParameterSettings'][number]['values'][number];
type MaterialInputSettingGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['materialInputSettings'][number];
type MaterialInputPlannedValueGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['materialInputSettings'][number]['values'][number];
type MaterialOutputSettingGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['materialOutputSettings'][number];
type EquipmentSettingGraphQL = ProcedureFlowchartVersionNode['nodes'][number]['equipmentSettings'][number];
type EquipmentPlannedValueGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['equipmentSettings'][number]['values'][number];

type ProcedureStepConfigGraphQL =
  ProcedureFlowchartVersionNode['nodes'][number]['stepGroups'][number]['steps'][number];
type StepGroupGraphQL = ProcedureFlowchartVersionNode['nodes'][number]['stepGroups'][number];
type ProcedureEdgeGraphQL = ProcedureFlowchartVersionNode['edges'][number];
type MethodNodeGraphQL = ProcedureFlowchartVersionNode['nodes'][number];
type MethodNodeParameterSettingGraphQL = Pick<
  FullProcedureMethodDefinitionVersionGraphQL,
  'dataParameterSettings' | 'materialInputSettings' | 'equipmentSettings' | 'materialOutputSettings'
>;

/** Accepts full GraphQL type or shape where libraryParameterDefinition is not included. */
export type NetworkMethodParameterSettings = Pick<
  MethodNodeParameterSettingGraphQL,
  'materialInputSettings' | 'materialOutputSettings' | 'equipmentSettings'
> & {
  dataParameterSettings: Array<NetworkDataParameterSetting>;
};

export type ParameterUsageNodeGraphQL = MethodNodeGraphQL['parameterUsagesExpensive'][number];
type AnalysisTemplateConfigGraphQL = MethodNodeGraphQL['analysisTemplateConfigurations'][number];
type TestDefinitionGraphQL = MethodNodeGraphQL['testDefinitions'][number];

function parseProcedureStepConfigFromNetwork(
  networkStepConfig: ProcedureStepConfigGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureStepConfig {
  const {id, name, documentVersion} = networkStepConfig;

  const maybeDocumentId =
    documentVersion != null && documentVersion.document != null ? documentVersion.document.id : null;

  return {
    id,
    cid: getCachedCid(id, errorContext),
    name,
    documentId: maybeDocumentId,
    fromId: documentVersion?.id ?? null,
    documentVersionUpdate: null,
  };
}

function parseStepGroupFromNetwork(
  networkStepGroup: StepGroupGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureStepGroup {
  const {group, steps} = networkStepGroup;
  return {
    group,
    steps: steps.map((step, stepIndexWithinGroup) =>
      parseProcedureStepConfigFromNetwork(step, getCachedCid, {
        ...errorContext,
        stepGroup: group,
        stepIndexWithinGroup,
      })
    ),
  };
}

export function parseParameterSettingsFromNetwork(
  networkMethodParameterSettingsNode: NetworkMethodParameterSettings,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ParameterSettings {
  return {
    dataParameterSettings: networkMethodParameterSettingsNode.dataParameterSettings.map((ps) =>
      parseDataParameterSettingFromNetwork(ps, getCachedCid, errorContext)
    ),
    materialInputSettings: networkMethodParameterSettingsNode.materialInputSettings.map((mis) =>
      parseMaterialInputSettingFromNetwork(mis, getCachedCid, errorContext)
    ),
    materialOutputSettings: networkMethodParameterSettingsNode.materialOutputSettings.map((mos) =>
      parseMaterialOutputSettingFromNetwork(mos, getCachedCid, errorContext)
    ),
    equipmentSettings: networkMethodParameterSettingsNode.equipmentSettings.map((es) =>
      parseEquipmentSettingFromNetwork(es, getCachedCid, errorContext)
    ),
  };
}

/**
 * Parses a data parameter's `dataType` from the loosely-typed (`JSONScalar`) network payload into a
 * domain `DataParameterType`. Returns null only when the payload is structurally unusable (missing,
 * not an object, or an unsupported field type) so the caller can fall back to a default.
 *
 * For dropdown (selector) parameters we intentionally preserve whatever `schemaFieldSelectorId` was
 * persisted -- even when it is missing or malformed -- instead of silently rewriting the parameter
 * to a decimal. Coercing to a decimal here would autosave over (and permanently lose) the user's
 * dropdown configuration; front-end validation surfaces the misconfiguration to the user instead.
 */
function parseDataParameterTypeFromNetwork(networkDataType: unknown): DataParameterType | null {
  if (networkDataType == null || typeof networkDataType !== 'object') {
    return null;
  }
  const record = networkDataType as Record<string, unknown>;
  const type = record.type;
  if (!isDataParameterFieldType(type)) {
    return null;
  }
  if (type === FieldType.SELECTOR) {
    const schemaFieldSelectorId = record.schemaFieldSelectorId;
    if (typeof schemaFieldSelectorId !== 'string') {
      logging.warn('Procedure data parameter has SELECTOR type with invalid schemaFieldSelectorId', {
        extra: {schemaFieldSelectorId},
      });
    }
    return {
      type: FieldType.SELECTOR,
      requiredLink: null,
      unit: null,
      schemaFieldSelectorId: typeof schemaFieldSelectorId === 'string' ? schemaFieldSelectorId : '',
    };
  }
  return omit(record, 'schemaFieldSelectorId') as unknown as DataParameterType;
}

function parseDataParameterSettingFromNetwork(
  networkParameterSetting: NetworkDataParameterSetting,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureDataParameterSetting {
  const version = networkParameterSetting.version;
  const dataType = parseDataParameterTypeFromNetwork(version.dataType) ?? DEFAULT_DATA_PARAMETER_TYPE;
  return {
    dataParameterVersion: {
      ...omit(networkParameterSetting.version, '__typename', 'libraryParameterDefinition'),
      dataType,
      cid: getCachedCid(networkParameterSetting.version.id, {
        ...errorContext,
        loadingInfo: 'dataParameter version',
      }),
      libraryParameterDefinition:
        version.libraryParameterDefinition != null
          ? {
              id: version.libraryParameterDefinition.id,
              name: version.libraryParameterDefinition.name,
            }
          : null,
    },
    values: networkParameterSetting.values.map((pv) =>
      parseParameterPlannedValueFromNetwork(pv, getCachedCid, {
        ...errorContext,
        loadingInfo: 'dataParameter planned value',
      })
    ),
  };
}

function parseMaterialInputSettingFromNetwork(
  networkMaterialInputSetting: MaterialInputSettingGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureMaterialInputSetting {
  return {
    materialInputVersion: {
      ...omit(networkMaterialInputSetting.version, '__typename'),
      cid: getCachedCid(networkMaterialInputSetting.version.id, {
        ...errorContext,
        loadingInfo: 'materialInput version',
      }),
    },
    values: networkMaterialInputSetting.values.map((miv) =>
      parseMaterialInputPlannedValueFromNetwork(miv, getCachedCid, {
        ...errorContext,
        loadingInfo: 'materialInput planned value',
      })
    ),
  };
}

function parseMaterialOutputSettingFromNetwork(
  networkMaterialOutputSetting: MaterialOutputSettingGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureMaterialOutputSetting {
  return {
    materialOutputVersion: {
      ...omit(networkMaterialOutputSetting.version, '__typename', 'confirmationMethod'),
      cid: getCachedCid(networkMaterialOutputSetting.version.id, {
        ...errorContext,
        loadingInfo: 'materialOutput version',
      }),
    },
  };
}

function parseEquipmentAllowedModelsFromNetwork(
  allowedEquipmentModels: JSONScalar | null,
  dataType: JSONScalar
): EquipmentTypedModel {
  // For equipment created before equipment models feature flag is set, an empty typed value is generated
  if (allowedEquipmentModels == null) {
    return dataType.type === FieldType.TEXT
      ? {type: FieldType.TEXT, value: ''}
      : {
          type: FieldType.BIOENTITY_LINK,
          value: '',
          targetFiles: [],
        };
  }
  return allowedEquipmentModels;
}

function parseEquipmentSettingFromNetwork(
  networkEquipmentSetting: EquipmentSettingGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureEquipmentSetting {
  return {
    equipmentVersion: {
      ...omit(networkEquipmentSetting.version, '__typename', 'confirmationMethod'),
      models: parseEquipmentAllowedModelsFromNetwork(
        networkEquipmentSetting.version.allowedEquipmentModels,
        networkEquipmentSetting.version.dataType
      ),
      cid: getCachedCid(networkEquipmentSetting.version.id, {
        ...errorContext,
        loadingInfo: 'equipment version',
      }),
    },
    values: networkEquipmentSetting.values.map((ev) =>
      parseEquipmentPlannedValueFromNetwork(ev, getCachedCid, {
        ...errorContext,
        loadingInfo: 'equipment planned value',
      })
    ),
  };
}

function parseParameterPlannedValueFromNetwork(
  parameterPlannedValue: DataParameterPlannedValueGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureDataParameterPlannedValue {
  return {
    ...omit(parameterPlannedValue, '__typename'),
    cid: getCachedCid(parameterPlannedValue.id, errorContext),
  };
}

function parseMaterialInputPlannedValueFromNetwork(
  materialInputPlannedValue: MaterialInputPlannedValueGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureMaterialInputPlannedValue {
  return {
    ...omit(materialInputPlannedValue, '__typename'),
    cid: getCachedCid(materialInputPlannedValue.id, errorContext),
  };
}

function parseEquipmentPlannedValueFromNetwork(
  equipmentPlannedValue: EquipmentPlannedValueGraphQL,
  getCachedCid: (id: string, errorContext: object) => string,
  errorContext: object
): ProcedureEquipmentPlannedValue {
  return {
    ...omit(equipmentPlannedValue, '__typename'),
    cid: getCachedCid(equipmentPlannedValue.id, errorContext),
  };
}

export function parseParameterUsagesFromNetwork(
  gqlParameterUsages: Array<ParameterUsageNodeGraphQL>,
  stepNamesAndCidsById: {[stepId: string]: {name: string; cid: string}}
): ParameterUsages {
  const parameterUsages = gqlParameterUsages.map((usage) => ({
    ...usage,
    stepCid: stepNamesAndCidsById[usage.stepId].cid,
    stepName: stepNamesAndCidsById[usage.stepId].name,
  }));
  return groupBy(parameterUsages, (parameterUsage) => parameterUsage.parameterVersionId);
}

function parseAnalysisTemplateConfigsFromNetwork(
  gqlAnalysisTemplateConfigs: Array<AnalysisTemplateConfigGraphQL>
): Array<ProcedureAnalysisTemplateConfiguration | InaccessibleProcedureAnalysisTemplateConfiguration> {
  return gqlAnalysisTemplateConfigs.map((templateConfig) =>
    !isInaccessible(templateConfig)
      ? {
          analysisTemplateId: templateConfig.analysisTemplate.id,
          config: parseAndValidateAnalysisTemplateConfigData(templateConfig.configJson),
        }
      : {invalidAnalysisTemplateId: templateConfig.invalidId}
  );
}

function parseTestDefinitionsFromNetwork(
  gqlTestDefinitions: Array<TestDefinitionGraphQL>
): Array<ProcedureTestDefinition | InaccessibleProcedureTestDefinition> {
  return gqlTestDefinitions.map((testDefinition) =>
    !isInaccessible(testDefinition)
      ? {
          id: testDefinition.id,
          name: testDefinition.name,
          description: testDefinition.description,
          resultSchemaName: isInaccessible(testDefinition.resultSchemaCorrect)
            ? '(No access)'
            : testDefinition.resultSchemaCorrect.name,
        }
      : {invalidId: testDefinition.invalidId}
  );
}

export function parseMethodNodeFromNetwork(
  networkMethodNode: MethodNodeGraphQL,
  getCachedCid: (id: string, errorContext: object) => string
): MethodNode {
  const {id, name, description, sourceMethodId, nodeId, provenanceId} = networkMethodNode;
  const errorContext = {methodId: id};

  const stepNamesAndCidsById = objectFromEntries(
    networkMethodNode.stepGroups.flatMap((sg) =>
      sg.steps.map(
        (step) =>
          [
            step.id,
            {
              name: step.name,
              cid: getCachedCid(step.id, {
                ...errorContext,
                loadingInfo: 'steps',
              }),
            },
          ] as [string, {name: string; cid: string}]
      )
    )
  );
  return {
    id,
    cid: getCachedCid(id, errorContext),
    type: ProcedureNodeType.METHOD,
    name,
    description,
    stepGroups: networkMethodNode.stepGroups.map((sg) =>
      parseStepGroupFromNetwork(sg, getCachedCid, errorContext)
    ),
    ...parseParameterSettingsFromNetwork(networkMethodNode, getCachedCid, errorContext),
    parameterUsages: parseParameterUsagesFromNetwork(
      networkMethodNode.parameterUsagesExpensive,
      stepNamesAndCidsById
    ),
    sourceMethodId,
    nodeId,
    provenanceId,
    canvasGridCoordinates: omit(networkMethodNode.gridLayoutCoordinates, '__typename'),
    analysisTemplateConfigurations: parseAnalysisTemplateConfigsFromNetwork(
      networkMethodNode.analysisTemplateConfigurations
    ),
    testDefinitions: parseTestDefinitionsFromNetwork(networkMethodNode.testDefinitions),
    dilutionPlan: omit(networkMethodNode.dilutionPlan, '__typename'),
  };
}

function parseMethodEdgeFromNetwork(sourceMethodCid: string, targetMethodCid: string): MethodEdge {
  const sourceHandle = createHandleIdForMethod(sourceMethodCid, 'source');
  const targetHandle = createHandleIdForMethod(targetMethodCid, 'target');
  return {
    id: createEdgeId(sourceHandle, targetHandle),
    type: ProcedureEdgeType.METHOD_EDGE,
    fromMethodCid: sourceMethodCid,
    toMethodCid: targetMethodCid,
    sourceHandle,
    targetHandle,
  };
}

function parseMaterialEdgeFromNetwork(
  sourceMaterialId: string,
  targetMaterialId: string,
  materialIdToMethodCid: Record<string, string>
): MaterialEdge {
  const sourceMethodCid = materialIdToMethodCid[sourceMaterialId];
  const targetMethodCid = materialIdToMethodCid[targetMaterialId];
  const sourceHandle = createHandleIdForMaterial(sourceMethodCid, sourceMaterialId, 'source');
  const targetHandle = createHandleIdForMaterial(targetMethodCid, targetMaterialId, 'target');
  return {
    id: createEdgeId(sourceHandle, targetHandle),
    type: ProcedureEdgeType.MATERIAL_EDGE,
    fromMethodCid: sourceMethodCid,
    toMethodCid: targetMethodCid,
    sourceHandle,
    targetHandle,
  };
}

function removeMaterialOutputPrefixIfPresent(materialId: string): string {
  return materialId.startsWith('matout-') ? materialId.split('matout-')[1] : materialId;
}

function removeMaterialInputPrefixIfPresent(materialId: string): string {
  return materialId.startsWith('matin-') ? materialId.split('matin-')[1] : materialId;
}

function parseProcedureEdgeFromNetwork(
  networkEdge: Pick<ProcedureEdgeGraphQL, 'type' | 'sourceId' | 'targetId'>,
  materialIdToMethodCid: Record<string, string>,
  getCachedCid: (id: string, errorContext: object) => string
): ProcedureEdge {
  const {type, sourceId, targetId} = networkEdge;
  switch (type) {
    case ProcedureEdgeType.METHOD_EDGE:
      return parseMethodEdgeFromNetwork(
        getCachedCid(sourceId, {methodEdge: 'source'}),
        getCachedCid(targetId, {methodEdge: 'target'})
      );
    case ProcedureEdgeType.MATERIAL_EDGE:
      return parseMaterialEdgeFromNetwork(
        // TODO(BNCH-90720): remove special casing for M0 material ids once M0 materials are removed
        // isUuid check is necessary so that we don't try fetching the cached cid for M0 material ids,
        // which would result in an assertion error and the user unable to view the procedure.
        // We are not checking the M1 parameters config flag here because we want to parse M1 material edges
        // correctly, or else they could risk being lost during a save.
        !isUuid(removeMaterialOutputPrefixIfPresent(sourceId))
          ? getCachedCid(sourceId, {materialEdge: 'source'})
          : sourceId,
        !isUuid(removeMaterialInputPrefixIfPresent(targetId))
          ? getCachedCid(targetId, {materialEdge: 'target'})
          : targetId,
        materialIdToMethodCid
      );
    default:
      assert(false, 'unsupported edge type!');
  }
}

function generateMaterialCidToMethodCidMap(
  nodes: Array<Pick<Method, 'cid' | 'materialInputSettings' | 'materialOutputSettings'>>
): {[materialCid: string]: string} {
  const materialAndMethodIds = nodes.flatMap(
    ({cid: methodCid, materialInputSettings, materialOutputSettings}) => {
      const materialInputSettingCidMaps = materialInputSettings.map((materialInputSetting) => ({
        materialCid: getMethodComponentCid(materialInputSetting),
        methodCid,
      }));
      const materialOutputSettingCidMaps = materialOutputSettings.map((materialOutputSetting) => ({
        materialCid: getMethodComponentCid(materialOutputSetting),
        methodCid,
      }));
      return [...materialInputSettingCidMaps, ...materialOutputSettingCidMaps];
    }
  );
  return mapObject(
    groupByUniqueKey(materialAndMethodIds, ({materialCid}) => materialCid),
    ({methodCid}) => methodCid
  );
}

function parseFlowchartFromNetwork(
  networkFlowchartVersion: ProcedurePageFlowchartVersionQuery['procedureFlowchartVersion'],
  getCachedCid: (id: string, errorContext: object) => string,
  procedureType: ProcedureType
): ProcedureWithMetadata['flowchart'] {
  const methodNamesInUse: Array<string> = [];
  if (isInaccessible(networkFlowchartVersion)) {
    return {nodes: [], edges: []};
  }

  const nodes = networkFlowchartVersion.nodes.map((n) => {
    // Ensure that all nodes have names. Old flowcharts allowed empty (and non-unique) names
    const newNode = parseMethodNodeFromNetwork(n, getCachedCid);
    if (validateMethodName(newNode.name, methodNamesInUse, procedureType) != null) {
      newNode.name = normalizeNameForUniqueness(
        newNode.name,
        methodNamesInUse,
        getMethodNamePlaceholder(ProcedureType.RECIPE)
      );
    }
    methodNamesInUse.push(newNode.name);
    return newNode;
  });
  const materialCidToMethodCid = generateMaterialCidToMethodCidMap(nodes);
  const edges = networkFlowchartVersion.edges
    .filter((e) =>
      e.type === ProcedureEdgeType.MATERIAL_EDGE
        ? // Since M1 parameters are turned on, we want to parse only M1 material edges. This means that
          // any subsequent updates to the procedure will drop M0 material edges.
          !isUuid(removeMaterialOutputPrefixIfPresent(e.sourceId)) &&
          !isUuid(removeMaterialInputPrefixIfPresent(e.targetId))
        : true
    )
    .map((e) => parseProcedureEdgeFromNetwork(e, materialCidToMethodCid, getCachedCid));
  return {nodes, edges};
}

export default function parseProcedureFromNetwork(
  networkProcedure: NetworkProcedure,
  getCachedCid: (id: string, errorContext: object) => string
): ProcedureWithMetadata {
  const {id, name, procedureType, archiveData, creator, createdAt, modifiedAt, permissions} =
    networkProcedure;
  // NOTE: This function does not need to be refactored to using the PoW latest flowchart version because in PoW
  // procedures we will no longer have to handle loading and rendering of the flowchart ourselves,
  // but rather let ProcessFlowchartConfigEditor handle the loading and rendering of the flowchart.
  return {
    flowchart: parseFlowchartFromNetwork(
      networkProcedure.procedureFlowchart.latestFlowchartVersion,
      getCachedCid,
      procedureType
    ),
    metadata: {
      id,
      name,
      procedureType,
      templateCollection: {
        id: networkProcedure.templateCollection.id,
        name: networkProcedure.templateCollection.name,
        url: networkProcedure.templateCollection.url,
        owner: pick(networkProcedure.templateCollection.ownerJson, 'id', 'name', 'type'),
      },
      publishingRecords: accessibleObjs(networkProcedure.publishingRecords),
      archiveData,
      creator,
      createdAt,
      modifiedAt,
      permissions,
    },
    processFlowchartConfigId: networkProcedure.processFlowchartConfig?.id ?? null,
  };
}
